import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { BRAND, SERVICE_OPTIONS } from "../mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast({
        title: "Message sent",
        description: `Thanks ${form.firstName || ""}! The Boulter Group team will be in touch shortly.`,
      });
    } catch (err) {
      const saved = err?.response?.status === 502;
      toast({
        title: saved ? "Enquiry received" : "Something went wrong",
        description: saved
          ? "We've logged your enquiry but email delivery is delayed. We'll still get back to you."
          : `Please try again, or call us on ${BRAND.phone}.`,
        variant: saved ? undefined : "destructive",
      });
      if (saved) setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="brand-texture bg-[#1F3463] pb-16 pt-48">
        <div className="mx-auto max-w-7xl px-5 text-center lg:px-8">
          <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">Get In Touch</span>
          <h1 className="mt-3 font-display text-4xl font-bold text-white sm:text-5xl">Contact us</h1>
          <p className="mx-auto mt-5 max-w-xl text-lg text-white/75">
            If you wish to get in touch, we'd be glad to chat about how we can
            help with your property plans.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-5">
            {/* INFO */}
            <div className="lg:col-span-2">
              <h2 className="font-display text-2xl font-bold text-[#1F3463]">Talk to our team</h2>
              <p className="mt-3 text-slate-600">
                Reach out by phone, email or pop into our office. We'd love to hear
                about your project.
              </p>
              <div className="mt-8 space-y-5">
                <a href={BRAND.phoneHref} className="card-lift flex items-start gap-4 rounded-xl border border-slate-100 bg-white p-5">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#F5F1E8] text-[#1F3463]"><Phone className="h-6 w-6" /></span>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Phone</div>
                    <div className="font-semibold text-[#1F3463]">{BRAND.phone}</div>
                  </div>
                </a>
                <a href={`mailto:${BRAND.email}`} className="card-lift flex items-start gap-4 rounded-xl border border-slate-100 bg-white p-5">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#F5F1E8] text-[#1F3463]"><Mail className="h-6 w-6" /></span>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Email</div>
                    <div className="font-semibold text-[#1F3463] break-all">{BRAND.email}</div>
                  </div>
                </a>
                <div className="flex items-start gap-4 rounded-xl border border-slate-100 bg-white p-5">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#F5F1E8] text-[#1F3463]"><MapPin className="h-6 w-6" /></span>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Office</div>
                    <div className="font-semibold text-[#1F3463]">{BRAND.address}</div>
                  </div>
                </div>
              </div>
              <div className="mt-8 overflow-hidden rounded-xl border border-slate-100">
                <iframe
                  title="Boulter Group location"
                  src="https://www.google.com/maps?q=Road+Farm,+Thaxted+Road,+Little+Sampford,+Saffron+Walden+CB10+2RZ&output=embed"
                  className="h-64 w-full"
                  loading="lazy"
                />
              </div>
            </div>

            {/* FORM */}
            <div className="lg:col-span-3">
              <div className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm">
                {submitted ? (
                  <div className="flex flex-col items-center py-16 text-center">
                    <CheckCircle2 className="h-16 w-16 text-[#C9A876]" />
                    <h3 className="mt-5 font-display text-2xl font-bold text-[#1F3463]">Thank you!</h3>
                    <p className="mt-3 max-w-md text-slate-600">
                      Your message has been received. The Boulter Group team
                      will be in touch very soon.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setForm({ firstName: "", lastName: "", email: "", phone: "", service: "", message: "" }); }}
                      className="btn-outline-navy mt-7 rounded-md px-6 py-3 text-sm font-semibold uppercase tracking-wide"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="First name" value={form.firstName} onChange={update("firstName")} required />
                      <Field label="Last name" value={form.lastName} onChange={update("lastName")} required />
                      <Field label="Email" type="email" value={form.email} onChange={update("email")} required />
                      <Field label="Phone number" type="tel" value={form.phone} onChange={update("phone")} />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[#1F3463]">Services interested in?</label>
                      <select
                        value={form.service}
                        onChange={update("service")}
                        className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-[#1F3463] focus:ring-2 focus:ring-[#1F3463]/20"
                      >
                        <option value="">Select a service…</option>
                        {SERVICE_OPTIONS.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[#1F3463]">Message</label>
                      <textarea
                        value={form.message}
                        onChange={update("message")}
                        rows={5}
                        required
                        className="w-full resize-none rounded-md border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-[#1F3463] focus:ring-2 focus:ring-[#1F3463]/20"
                        placeholder="Tell us about your project…"
                      />
                    </div>
                    <button type="submit" disabled={submitting} className="btn-navy flex w-full items-center justify-center gap-2 rounded-md px-6 py-4 text-sm font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto">
                      {submitting ? (
                        <>Sending <Loader2 className="h-4 w-4 animate-spin" /></>
                      ) : (
                        <>Submit <Send className="h-4 w-4" /></>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-[#1F3463]">
        {label}{required && <span className="text-[#C9A876]"> *</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-[#1F3463] focus:ring-2 focus:ring-[#1F3463]/20"
      />
    </div>
  );
}
